import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { getStoredPageSize, setStoredPageSize } from '@/components/Pagination';
import { supabase } from '@/lib/supabase';
import { getStatusString, REQUEST_STATUS } from '@/lib/attendanceTypes';

/**
 * Hook to load all requests page data with server-side pagination and filtering
 * Replaces 2 sequential queries + client-side filtering with 1 optimized RPC call
 *
 * @param {string} companyId - Company ID
 * @param {Object} options - Configuration options
 * @param {number} options.pageSize - Items per page (default 25)
 * @param {number} options.debounceMs - Search debounce delay (default 300ms)
 * @returns {Object} - { requests, stats, loading, error, pagination, filters, refetch }
 */
export function useAllRequests(companyId, options = {}) {
  const { pageSize: initialPageSize = 25, debounceMs = 300 } = options;

  // Data state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, _setPageSize] = useState(() => getStoredPageSize(initialPageSize));
  const setPageSize = useCallback((val) => { _setPageSize(val); setStoredPageSize(val); }, []);

  // Filter state - default to pending
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Refs for request management
  const loadingRef = useRef(false);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Debounce search term
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on search
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, debounceMs]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, typeFilter]);

  /**
   * Load requests data from server
   */
  const loadData = useCallback(async () => {
    if (!companyId) {
      console.log('[useAllRequests] Missing companyId');
      setLoading(false);
      return null;
    }

    // Prevent concurrent requests
    if (loadingRef.current) {
      console.log('[useAllRequests] Already loading, skipping...');
      return null;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[useAllRequests] Loading data with single RPC call...');
      console.time('[useAllRequests] RPC call duration');

      const offset = (page - 1) * pageSize;

      // Build filter params - 'all' means no filter
      const statusParam = statusFilter && statusFilter !== 'all' ? statusFilter : null;
      const typeParam = typeFilter && typeFilter !== 'all' ? typeFilter : null;
      const searchParam = debouncedSearch || null;

      // Single RPC call replaces 2 queries + client-side filtering!
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_all_requests_page',
        {
          p_company_id: companyId,
          p_status: statusParam,
          p_leave_type: typeParam,
          p_search: searchParam,
          p_limit: pageSize,
          p_offset: offset
        }
      );

      console.timeEnd('[useAllRequests] RPC call duration');

      if (rpcError) {
        console.error('[useAllRequests] RPC error:', rpcError);
        setError(rpcError.message);
        setData(null);
        return null;
      }

      console.log('[useAllRequests] RPC response:', {
        requestsCount: rpcData?.requests?.length || 0,
        totalCount: rpcData?.total_count || 0,
        filteredCount: rpcData?.filtered_count || 0,
        stats: rpcData?.stats
      });

      setData(rpcData);
      return rpcData;
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return null;
      }
      console.error('[useAllRequests] Error:', err);
      setError(err.message);
      setData(null);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [companyId, page, pageSize, statusFilter, typeFilter, debouncedSearch]);

  // Load data when dependencies change
  useEffect(() => {
    loadData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadData]);

  /**
   * Change page size and reset to first page
   */
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  /**
   * Set status filter (converts integer ID to string if needed)
   */
  const handleStatusChange = useCallback((status) => {
    // Convert integer status ID to string if needed
    if (typeof status === 'number') {
      status = getStatusString(status);
    }
    // Handle string integer IDs like "100101"
    if (typeof status === 'string' && /^\d+$/.test(status)) {
      status = getStatusString(parseInt(status, 10));
    }
    setStatusFilter(status);
  }, []);

  /**
   * Set type filter
   */
  const handleTypeChange = useCallback((type) => {
    setTypeFilter(type === 'all' ? null : type);
  }, []);

  // Computed pagination values
  const totalPages = useMemo(() => {
    const filteredCount = data?.filtered_count || 0;
    return Math.ceil(filteredCount / pageSize);
  }, [data?.filtered_count, pageSize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // Data
    requests: data?.requests || [],
    stats: data?.stats || { total: 0, pending: 0, approved: 0, rejected: 0, withdrawn: 0 },

    // Loading state
    loading,
    error,

    // Pagination
    pagination: {
      page,
      pageSize,
      totalPages,
      totalItems: data?.total_count || 0,
      filteredItems: data?.filtered_count || 0,
      setPage,
      setPageSize: handlePageSizeChange
    },

    // Filters
    filters: {
      status: statusFilter,
      type: typeFilter,
      search: searchTerm,
      setStatus: handleStatusChange,
      setType: handleTypeChange,
      setSearch: setSearchTerm
    },

    // Actions
    refetch: loadData
  };
}

/**
 * Hook for request actions (approve, reject, withdraw)
 * Separated to keep concerns clean
 *
 * @param {Function} onSuccess - Callback after successful action
 * @returns {Object} - { approveRequest, rejectRequest, withdrawRequest, bulkWithdraw, processing }
 */
export function useRequestActions(onSuccess) {
  const [processing, setProcessing] = useState(false);

  /**
   * Update a single request status
   */
  const updateRequestStatus = useCallback(async (request, newStatus, adminNote = null, userProfile = null) => {
    if (!request?.id) return false;

    setProcessing(true);

    try {
      const updates = {
        status: typeof newStatus === 'number' ? getStatusString(newStatus) : newStatus,
        updated_date: new Date().toISOString()
      };

      // Add decider info for approve/reject
      if (newStatus === 'approved' || newStatus === 'rejected' ||
          newStatus === REQUEST_STATUS.APPROVED || newStatus === REQUEST_STATUS.REJECTED) {
        if (userProfile?.id) {
          updates.decided_by = userProfile.id;
          updates.decided_date = new Date().toISOString();
        }
      }

      // Add admin note for rejections
      if (adminNote) {
        updates.admin_note = adminNote;
      }

      const { error } = await supabase
        .from('leave_requests')
        .update(updates)
        .eq('id', request.id);

      if (error) throw error;

      if (onSuccess) onSuccess();
      return true;
    } catch (err) {
      console.error('[useRequestActions] Error:', err);
      return false;
    } finally {
      setProcessing(false);
    }
  }, [onSuccess]);

  /**
   * Approve a request
   */
  const approveRequest = useCallback(async (request, userProfile) => {
    return updateRequestStatus(request, 'approved', null, userProfile);
  }, [updateRequestStatus]);

  /**
   * Reject a request with reason
   */
  const rejectRequest = useCallback(async (request, reason, userProfile) => {
    return updateRequestStatus(request, 'rejected', reason, userProfile);
  }, [updateRequestStatus]);

  /**
   * Withdraw a request
   */
  const withdrawRequest = useCallback(async (request) => {
    return updateRequestStatus(request, 'withdrawn');
  }, [updateRequestStatus]);

  /**
   * Bulk withdraw multiple requests
   */
  const bulkWithdraw = useCallback(async (requestIds) => {
    if (!requestIds?.length) return false;

    setProcessing(true);

    try {
      const { error } = await supabase
        .from('leave_requests')
        .update({
          status: 'withdrawn',
          updated_date: new Date().toISOString()
        })
        .in('id', requestIds);

      if (error) throw error;

      if (onSuccess) onSuccess();
      return true;
    } catch (err) {
      console.error('[useRequestActions] Bulk withdraw error:', err);
      return false;
    } finally {
      setProcessing(false);
    }
  }, [onSuccess]);

  return {
    approveRequest,
    rejectRequest,
    withdrawRequest,
    bulkWithdraw,
    processing
  };
}
